export function useAdminApi(config, auth) {
  const base = config.apiBaseUrl;

  /* 
  ✅ MOCK 開關改用環境變數控制
  - 好處：不用改程式碼就能切換 mock / real
  - Vue CLI 讀取環境變數必須以 VUE_APP_ 開頭
  - 預設值設為 "true"：避免你忘記設定就直接打到真後端（比較安全）
  */
  const MOCK = String(process.env.VUE_APP_ADMIN_MOCK || "true") === "true";

  // mock 日期以「今天 + N 天」產生，月曆上永遠看得到
  function mockDate(offsetDays) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  }

  function mockUnavailable() {
    return {
      fullDay: [mockDate(3)],
      slots: [
        // 第二個是舊格式字串：用來確認月曆儲存時不會把它弄丟
        { date: mockDate(4), timeSlots: ["09:00(線上)", "19:00-20:00(線上)"] },
        { date: mockDate(6), timeSlots: ["22:00(線上)"] },
      ],
    };
  }

  function mockBookings() {
    const now = new Date().toISOString();
    return [
      {
        id: "A001",
        date: mockDate(2),
        timeSlot: "14:00(線上)",
        serviceId: "bazi",
        name: "王小明",
        contact: "LINE: abc123",
        status: "pending",
        createdAt: now,
      },
      {
        id: "A002",
        date: mockDate(5),
        timeSlots: ["17:00(線上)", "19:00(線上)"],
        serviceId: "liuyao",
        name: "陳小美",
        contact: "電話: 09xx-xxx-xxx",
        status: "done",
        createdAt: now,
      },
      {
        id: "A003",
        date: mockDate(5),
        timeSlot: "09:00(線上)",
        serviceId: "name",
        name: "已取消的客人",
        contact: "LINE: xyz",
        status: "canceled",
        createdAt: now,
      },
    ];
  }

  async function request(path, { method = "GET", token = "", json } = {}) {
    const headers = { ...auth.buildAuthHeaders(token) };
    if (json !== undefined) headers["Content-Type"] = "application/json";

    const res = await fetch(`${base}${path}`, {
      method,
      headers,
      body: json !== undefined ? JSON.stringify(json) : undefined,
    });

    const ct = res.headers.get("content-type") || "";
    const data = ct.includes("application/json")
      ? await res.json().catch(() => null)
      : await res.text().catch(() => null);

    if (!res.ok) {
      const msg = (data && data.message) || `HTTP ${res.status}`;
      const err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  async function verifyToken(token) {
    if (MOCK) return mockUnavailable();
    return request(config.auth.verifyEndpoint, { token });
  }

  async function getUnavailable(token) {
    if (MOCK) return mockUnavailable();
    return request(config.unavailable.getEndpoint, { token });
  }

  async function saveUnavailable(token, payload) {
    if (MOCK) return { success: true };
    return request(config.unavailable.saveEndpoint, {
      method: "POST",
      token,
      json: payload,
    });
  }

  async function listBookings(token) {
    if (MOCK) return mockBookings();
    return request(config.bookings.listEndpoint, { token });
  }

  async function updateBookingStatus(token, id, status) {
    if (MOCK) return { success: true };
    return request(config.bookings.updateStatusEndpoint(id), {
      method: "PATCH",
      token,
      json: { status },
    });
  }

  async function deleteBooking(token, id) {
    if (MOCK) return { success: true };
    return request(config.bookings.deleteEndpoint(id), {
      method: "DELETE",
      token,
    });
  }

  /* 
  ==========================================
  Admin API - user_access CRUD（前端封裝）
  ==========================================
  ✅ 目的：
  - 讓 component 不要到處拼 URL
  - 之後接案換 API path/host 只改一處
*/
  async function listUserAccess({ page = 1, pageSize = 10, q = "" } = {}) {
    /* 
    ✅ 目的：支援分頁 + 搜尋
    - q 會做 encode，避免特殊字元出事
  */
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("pageSize", String(pageSize));
    if (q) qs.set("q", q);

    return request(`/api/admin/user-access?${qs.toString()}`, {
      method: "GET",
    });
  }

  async function getUserAccess(userId) {
    /* 
    ✅ 目的：取得單筆資料（編輯前刷新）
  */
    return request(`/api/admin/user-access/${encodeURIComponent(userId)}`, {
      method: "GET",
    });
  }

  async function patchUserAccess(userId, payload) {
    /* 
    ✅ 目的：更新 first_free / quota
    - payload 格式：
      { first_free: {...}, quota: {...} }
  */
    return request(`/api/admin/user-access/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  }

  async function deleteUserAccess(userId) {
    /* 
    ✅ 目的：整筆刪除
  */
    return request(`/api/admin/user-access/${encodeURIComponent(userId)}`, {
      method: "DELETE",
    });
  }

  async function createUserAccess(userId) {
    /* 
    ✅ 目的：新增一筆（用預設值）
  */
    return request(`/api/admin/user-access`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    });
  }

  return {
    verifyToken,
    getUnavailable,
    saveUnavailable,
    listBookings,
    updateBookingStatus,
    deleteBooking,
    // 讓你方便看現在是不是 mock
    isMock: () => MOCK,
    ////資料庫管理
    request,
    listUserAccess,
    getUserAccess,
    patchUserAccess,
    deleteUserAccess,
    createUserAccess,
  };
}
